/**
 * The Fountain API, as much of it as Table Talk needs. Every call carries the
 * bearer key; every error is an `ApiError` with the server's `error` string
 * when there was one. Ported (slimmed) from jhgaylor/dns-desk.
 */
import type { Agent, Conversation, LogEvent, Turn } from "./types";
import { readSse, type SseMessage } from "../lib/sse";
import type { Settings } from "../lib/settings";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string | null,
    message: string,
    public retryAfter: number | null = null,
    public body: Record<string, unknown> | null = null,
  ) {
    super(message);
  }
}

export class FountainClient {
  constructor(private settings: Settings) {}

  get baseUrl(): string {
    return this.settings.baseUrl;
  }

  // ── the analyst's conversations: one per dataset, on the table-talk channel ─

  /** The conversation currently bound to `channel` for this agent, if it is live. */
  async findConversation(agentId: string, channel: string): Promise<Conversation | null> {
    const qs = new URLSearchParams({ agent_id: agentId, channel_id: channel, status: "pending,running,idle", limit: "1" });
    return (await this.json<{ data: Conversation[] }>("GET", `/api/conversations?${qs}`)).data[0] ?? null;
  }

  /**
   * A new dataset: `fresh: true` rotates the channel's binding to a new
   * conversation (the old one keeps running until it is retired) with
   * `prompt` as turn 1.
   */
  async startFresh(input: { agent_id: string; channel_id: string; prompt: string }): Promise<Conversation> {
    const res = await this.json<{ data: Conversation; meta?: { resumed?: boolean } }>("POST", "/api/conversations", { ...input, fresh: true });
    if (res.meta?.resumed) await this.prompt(res.data.id, input.prompt);
    return res.data;
  }

  /** A follow-up on a dataset's own conversation. */
  prompt(conversationId: string, prompt: string): Promise<unknown> {
    return this.json("POST", `/api/conversations/${conversationId}/prompts`, { prompt });
  }

  getAgent(agentId: string): Promise<Agent> {
    return this.json<{ data: Agent }>("GET", `/api/agents/${agentId}`).then((r) => r.data);
  }

  // ── set-up: creating the analyst agent ────────────────────────────────────

  async findAgents(name: string): Promise<Agent[]> {
    const qs = new URLSearchParams({ search: name });
    return (await this.json<{ data: Agent[] }>("GET", `/api/agents?${qs}`)).data;
  }

  createAgent(input: { name: string; model: string; runtime: string; description?: string; system?: string }): Promise<Agent> {
    return this.json<{ data: Agent }>("POST", "/api/agents", input).then((r) => r.data);
  }

  /** Keep a reused agent's operating rules current with this build's spec. */
  updateAgent(agentId: string, input: { description?: string; system?: string }): Promise<Agent> {
    return this.json<{ data: Agent }>("PUT", `/api/agents/${agentId}`, input).then((r) => r.data);
  }

  // ── the conversations datasets live in ────────────────────────────────────

  async listTurns(conversationId: string): Promise<Turn[]> {
    return (await this.json<{ data: Turn[] }>("GET", `/api/conversations/${conversationId}/turns`)).data;
  }

  /** Every event of the conversation on the given streams, oldest first, paging until drained. */
  async listAllEvents(conversationId: string, streams: string[]): Promise<LogEvent[]> {
    const out: LogEvent[] = [];
    let after: number | null = null;
    for (;;) {
      const qs = new URLSearchParams({ limit: "1000", streams: streams.join(",") });
      if (after !== null) qs.set("after", String(after));
      const page: { data: LogEvent[]; meta: { has_more: boolean; next_cursor: number | null } } =
        await this.json("GET", `/api/conversations/${conversationId}/events?${qs}`);
      out.push(...page.data);
      if (!page.meta.has_more || page.meta.next_cursor === null) break;
      after = page.meta.next_cursor;
    }
    return out;
  }

  getConversation(conversationId: string): Promise<Conversation> {
    return this.json<{ data: Conversation }>("GET", `/api/conversations/${conversationId}`).then((r) => r.data);
  }

  markRead(conversationId: string): Promise<void> {
    return this.json<void>("POST", `/api/conversations/${conversationId}/read`);
  }

  // ── stream ────────────────────────────────────────────────────────────────

  /**
   * Every conversation's events on one connection (`/api/events/stream`);
   * the caller keeps the active dataset's. Resolves when the server closes
   * (idle timeout, deploy); the caller reconnects with the last id.
   */
  streamEvents(opts: {
    lastEventId: string | null;
    streams: string[];
    signal: AbortSignal;
    onMessage: (msg: SseMessage) => void;
    onOpen?: () => void;
    onClose: (err?: unknown) => void;
  }): Promise<void> {
    const qs = new URLSearchParams({ streams: opts.streams.join(",") });
    return readSse(`${this.baseUrl}/api/events/stream?${qs}`, {
      headers: { authorization: `Bearer ${this.settings.apiKey}` },
      lastEventId: opts.lastEventId,
      signal: opts.signal,
      onMessage: opts.onMessage,
      onOpen: opts.onOpen,
      onClose: opts.onClose,
    });
  }

  // ── plumbing ──────────────────────────────────────────────────────────────

  private async json<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    const headers: Record<string, string> = {
      authorization: `Bearer ${this.settings.apiKey}`,
      accept: "application/json",
    };
    if (body !== undefined) headers["content-type"] = "application/json";
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let parsed: unknown = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
    }
    if (!res.ok) {
      const obj = (parsed ?? {}) as { error?: unknown; message?: unknown };
      const code = typeof obj.error === "string" ? obj.error : null;
      const message = typeof obj.message === "string" ? obj.message : code ?? `${res.status} ${res.statusText}`;
      const ra = res.headers.get("retry-after");
      throw new ApiError(res.status, code, message, ra ? Number(ra) : null, parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null);
    }
    return parsed as T;
  }
}

/** A human line for an API failure, in the app's voice. */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "conversation_busy":
        return "The analyst is still working on the last question — one moment.";
      case "provisioning":
        return "The analyst's computer is still starting — try again shortly.";
      case "conversation_terminated":
        return "That analysis has ended — drop the file again to start fresh.";
      case "not_found":
        return "Not found.";
      default:
        if (err.status === 401) return "That API key was not accepted.";
        if (err.status === 429) return "Too many requests — slow down a little.";
        if (err.status === 503) return "Fountain could not reach the sandbox provider — try again shortly.";
        return err.message;
    }
  }
  if (err instanceof TypeError) {
    return "Could not reach Fountain. Check the URL, and that API_CORS_ORIGINS on the server includes this site.";
  }
  return err instanceof Error ? err.message : String(err);
}
